import { UserDTO } from "@/types/UserDTO";
import { User } from "@/types/User";
import type { DissolvePreflightKeys } from "@/components/PDeleteCompany/dissolveText";
import { lossesAckQuery, type LossesAck } from "@/lib/legal/dissolveLossesAck";
import { apiDelete, apiGet, apiPost } from "../_apiShim";
const BASE_PATH = "/api/v1/user/";

// PB-CDP / PB-CDP-7: a solo owner's account delete dissolves the
// organisation, and the doors refuse a dissolve that forfeits purchased event
// plans or balances unless the caller acknowledges the loss notice — query
// params (the backend's `@QueryParam("acknowledgeLosses")`, absent = false,
// plus the ticked acknowledgement's `lossesAck*`), sent only with one.

// Update user attributes
export async function updateUserAttributes(
  user: User,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}update-attributes`,
    {
      firstName: user?.firstName,
      lastName: user.lastName,
      monogramColor: user.monogramColor,
      languageCode: user.languageCode,
      acceptCrossCompanyEventInvites: user.acceptCrossCompanyEventInvites,
      theme: user.theme,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Get user attributes
export async function getUser(
  fields: string,
): Promise<User> {
  const response = await apiGet<User>(
    `${BASE_PATH}get-user?fields=${fields}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  if (!response) {
    throw new Error("User not found");
  }

  return response;
}

// Get user attributes
export async function getUserDTO(
  companyId: string,
): Promise<UserDTO> {
  const response = await apiGet<UserDTO>(
    `${BASE_PATH}get-user-dto/${companyId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  if (!response) {
    throw new Error("User not found");
  }

  return response;
}

// Delete user by ID
export async function deleteUserById(userId: string): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${userId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * What self-deletion means for this user, before they commit to it.
 *
 * An OWNER with colleagues must nominate a successor (the backend refuses
 * otherwise, so the picker is not optional chrome); a solo OWNER takes the
 * whole company down with them. Everyone else deletes only themselves.
 */
export type AccountDeletionPreflight = {
  role: string | null;
  requiresSuccessor: boolean;
  willDissolveCompany: boolean;
  hasActiveSubscriptions: boolean;
  candidates: {
    userId: string;
    email: string | null;
    name: string | null;
    role: string | null;
    iconColor: string | null;
  }[];
} & Partial<DissolvePreflightKeys>;
// ↑ PB-CDP: a new backend adds the dissolve keys on the solo-owner (DISSOLVE)
//   branch only — `dissolveCheckFailed` (the marker), `dissolveBlock`,
//   `dissolveLosses`. Absent on an old backend and on every other branch.

export async function getAccountDeletionPreflight(): Promise<AccountDeletionPreflight> {
  return await apiGet<AccountDeletionPreflight>(
    `${BASE_PATH}deletion-preflight`,
    { "Cache-Control": "no-store" },
    true,
  );
}

// Request account deletion (sends OTP)
export async function requestAccountDeletion(
  successorUserId: string | null | undefined,
  lossesAck: LossesAck | null,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}request-deletion${lossesAckQuery(lossesAck, "?")}`,
    { "Cache-Control": "no-store" },
    // Body, not a query param: the successor is a colleague's user id and has
    // no business in access logs. Always sent (even as null) so the shape is
    // one thing rather than two.
    { successorUserId: successorUserId ?? null },
  );
}

// Confirm account deletion with OTP
export async function confirmAccountDeletion(
  otp: string,
  successorUserId: string | null | undefined,
  // Re-sent like the successor: the confirm door re-checks the losses and
  // records the acknowledgement.
  lossesAck: LossesAck | null,
): Promise<boolean> {
  // PR-A4 / H8 — OTP in X-Otp header, not query string. Backend was
  // updated to @HeaderParam in parallel.
  return await apiDelete<boolean>(
    `${BASE_PATH}confirm-deletion${lossesAckQuery(lossesAck, "?")}`,
    {
      "Cache-Control": "no-store",
      "X-Otp": otp,
    },
    // Re-sent rather than remembered server-side: the backend re-validates the
    // successor at confirm time, because they can leave inside the OTP window.
    { successorUserId: successorUserId ?? null },
  );
}
